import { useEffect, useState } from "react";
import { useCanWrite } from "../auth/me";

// Inline sweep-priority editor: a compact [- N +] pill. Higher = scraped first, floored
// at 0. The local `draft` is the working value (optimistic): the buttons step off it, not
// the possibly-stale `value` prop, and commit persists only when the number actually
// changed. `onMouseDown preventDefault` keeps the input focused across a step so its blur
// never races a competing commit. `draft` resyncs when the server value changes. The
// number reads muted at the all-zero default so the column stays quiet.
export function PriorityStepper({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled?: boolean;
  onCommit: (v: number) => void;
}) {
  const canWrite = useCanWrite();
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const current = Math.max(0, Math.floor(Number(draft) || 0));
  const commit = (n: number) => {
    const v = Math.max(0, Math.floor(n || 0));
    setDraft(String(v));
    if (v !== value) onCommit(v);
  };
  if (!canWrite) return <span className={value <= 0 ? "faint" : undefined}>{value}</span>;
  return (
    <div className={`prio${current <= 0 ? " prio--zero" : ""}`}>
      <button
        type="button"
        disabled={disabled || current <= 0}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => commit(current - 1)}
        aria-label="decrease priority"
      >
        -
      </button>
      <input
        type="number"
        min={0}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={() => commit(current)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        disabled={disabled}
        aria-label="priority"
      />
      <button
        type="button"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => commit(current + 1)}
        aria-label="increase priority"
      >
        +
      </button>
    </div>
  );
}
